import { useState } from 'react';

import { toPersianDigits } from '@bookora/shared';

import {
  Card,
  JalaliDate,
  Numeric,
  QueryError,
  Space,
  StatusTag,
  Table,
  TruncatedText,
  Typography,
  type ColumnsType,
  type StatusTone,
} from '@bookora/ui';
import { useQuery } from '@tanstack/react-query';

import { listSupportAccess } from '@/api/endpoints/auth';
import { queryKeys } from '@/api/query-keys';
import type { SupportAccessRow } from '@/api/types';
import { useAuth } from '@/auth/token-store';
import { TableSkeleton } from '@/components/form-bits';

const { Text, Paragraph } = Typography;

const STATUS: Record<SupportAccessRow['status'], { tone: StatusTone; label: string }> = {
  LIVE: { tone: 'progress', label: 'در جریان' },
  CLOSED: { tone: 'success', label: 'بسته شد' },
  EXPIRED: { tone: 'inert', label: 'منقضی شد' },
};

/**
 * Every time Bookora support opened this clinic's panel (`GET
 * /security/support-access`, `audit.view`). The employee is never named — the
 * server sends a fixed label — only when, why, as whom and how much changed.
 */
export function SupportAccessCard() {
  const { activeClinicId } = useAuth();
  const [page, setPage] = useState(1);

  const log = useQuery({
    queryKey: queryKeys.supportAccess(activeClinicId, page),
    queryFn: ({ signal }) => listSupportAccess(page, signal),
    staleTime: 30_000,
  });

  const columns: ColumnsType<SupportAccessRow> = [
    {
      title: 'زمان ورود',
      dataIndex: 'startedAt',
      render: (value: string) => <JalaliDate value={value} variant="dateTime" />,
    },
    {
      title: 'وضعیت',
      dataIndex: 'status',
      render: (status: SupportAccessRow['status'], row) => (
        <Space size={4} wrap>
          <StatusTag tone={STATUS[status].tone}>{STATUS[status].label}</StatusTag>
          {row.endedAt ? <JalaliDate value={row.endedAt} variant="dateTime" /> : null}
        </Space>
      ),
    },
    {
      title: 'دلیل',
      dataIndex: 'reason',
      render: (reason: string | null) =>
        reason ? <TruncatedText>{reason}</TruncatedText> : <Text type="secondary">—</Text>,
    },
    {
      title: 'به‌جای',
      dataIndex: 'actingAs',
      responsive: ['md'],
      render: (name: string | null) => name ?? <Text type="secondary">—</Text>,
    },
    {
      title: 'دسترسی',
      dataIndex: 'allowWrites',
      render: (allowWrites: boolean) => (
        <StatusTag tone={allowWrites ? 'warning' : 'inert'}>
          {allowWrites ? 'امکان ثبت تغییر' : 'فقط مشاهده'}
        </StatusTag>
      ),
    },
    {
      title: 'تغییرها',
      dataIndex: 'writeCount',
      render: (count: number) => <Numeric>{toPersianDigits(count)}</Numeric>,
    },
  ];

  return (
    <Card title="ورود پشتیبانی بوکورا">
      <Paragraph type="secondary">
        هر بار که پشتیبانی بوکورا برای رسیدگی به درخواست شما وارد پنل شده، این‌جا ثبت می‌شه.
      </Paragraph>
      {log.isLoading ? <TableSkeleton rows={3} label="در حال بارگذاری سابقه…" /> : null}
      {log.error ? <QueryError error={log.error} onRetry={() => void log.refetch()} /> : null}
      {log.data ? (
        <Table<SupportAccessRow>
          rowKey="sessionId"
          dataSource={log.data.items}
          columns={columns}
          scroll={{ x: 560 }}
          pagination={{
            current: log.data.page,
            pageSize: log.data.pageSize,
            total: log.data.total,
            hideOnSinglePage: true,
            showSizeChanger: false,
            onChange: setPage,
          }}
          locale={{ emptyText: 'پشتیبانی تا حالا وارد پنل شما نشده.' }}
        />
      ) : null}
    </Card>
  );
}
